<template>
  <transition name="cfo-fade">
    <div v-if="visible" class="cfo-root" @mousemove.stop="onMouseMove" @mouseup.stop="onMouseUp" @touchmove.prevent="onTouchMove" @touchend.stop="onTouchEnd">

      <!-- Instrucción inicial (desaparece tras interacción) -->
      <transition name="cfo-tip-fade">
        <div v-if="showTip" class="cfo-tip">
          <v-icon size="16" color="white" class="mr-1">mdi-crop</v-icon>
          Arrastra y ajusta el recuadro para encuadrar el emplazamiento, luego presiona <strong>Capturar</strong>
        </div>
      </transition>

      <!-- Oscurece todo EXCEPTO el frame -->
      <!-- Top -->
      <div class="cfo-mask cfo-mask--top"    :style="maskTop"    @mousedown.stop @touchstart.stop />
      <!-- Bottom -->
      <div class="cfo-mask cfo-mask--bottom" :style="maskBottom" @mousedown.stop @touchstart.stop />
      <!-- Left -->
      <div class="cfo-mask cfo-mask--left"   :style="maskLeft"   @mousedown.stop @touchstart.stop />
      <!-- Right -->
      <div class="cfo-mask cfo-mask--right"  :style="maskRight"  @mousedown.stop @touchstart.stop />

      <!-- Frame recortable -->
      <div
        class="cfo-frame"
        :style="frameStyle"
        @mousedown.stop="onFrameMouseDown"
        @touchstart.prevent.stop="onFrameTouchStart"
      >
        <!-- Esquinas de redimensión -->
        <div class="cfo-handle cfo-handle--nw" @mousedown.stop="startResize('nw', $event)" @touchstart.prevent.stop="startResizeTouch('nw', $event)" />
        <div class="cfo-handle cfo-handle--ne" @mousedown.stop="startResize('ne', $event)" @touchstart.prevent.stop="startResizeTouch('ne', $event)" />
        <div class="cfo-handle cfo-handle--sw" @mousedown.stop="startResize('sw', $event)" @touchstart.prevent.stop="startResizeTouch('sw', $event)" />
        <div class="cfo-handle cfo-handle--se" @mousedown.stop="startResize('se', $event)" @touchstart.prevent.stop="startResizeTouch('se', $event)" />
        <!-- Lados de redimensión -->
        <div class="cfo-edge cfo-edge--n"  @mousedown.stop="startResize('n', $event)"  @touchstart.prevent.stop="startResizeTouch('n', $event)" />
        <div class="cfo-edge cfo-edge--s"  @mousedown.stop="startResize('s', $event)"  @touchstart.prevent.stop="startResizeTouch('s', $event)" />
        <div class="cfo-edge cfo-edge--w"  @mousedown.stop="startResize('w', $event)"  @touchstart.prevent.stop="startResizeTouch('w', $event)" />
        <div class="cfo-edge cfo-edge--e"  @mousedown.stop="startResize('e', $event)"  @touchstart.prevent.stop="startResizeTouch('e', $event)" />

        <!-- Regla de tercios -->
        <div class="cfo-grid">
          <div class="cfo-grid__v cfo-grid__v--1" />
          <div class="cfo-grid__v cfo-grid__v--2" />
          <div class="cfo-grid__h cfo-grid__h--1" />
          <div class="cfo-grid__h cfo-grid__h--2" />
        </div>

        <!-- Badge de dimensiones -->
        <div class="cfo-dimensions">{{ Math.round(frame.w) }} × {{ Math.round(frame.h) }}</div>
      </div>

      <!-- Barra de acciones -->
      <div class="cfo-actions">
        <button class="cfo-btn cfo-btn--cancel" @click.stop="$emit('cancel')">
          <v-icon size="14" class="mr-1">mdi-close</v-icon>
          Cancelar
        </button>
        <button class="cfo-btn cfo-btn--capture" :disabled="capturing" @click.stop="doCapture">
          <v-icon size="14" class="mr-1">{{ capturing ? 'mdi-loading mdi-spin' : 'mdi-camera' }}</v-icon>
          {{ capturing ? 'Capturando...' : 'Capturar' }}
        </button>
      </div>

    </div>
  </transition>
</template>

<script>
const MIN_SIZE = 80

export default {
  name: 'CaptureFrameOverlay',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // Referencia al elemento DOM del viewer (canvas o div Forge)
    viewerEl: {
      default: null
    },
    // 'image' | 'forge'
    viewerType: {
      type: String,
      default: 'image'
    }
  },

  data () {
    const pw = window.innerWidth
    const ph = window.innerHeight
    const w  = Math.round(pw * 0.55)
    const h  = Math.round(ph * 0.55)
    return {
      frame: {
        x: Math.round((pw - w) / 2),
        y: Math.round((ph - h) / 2),
        w,
        h
      },
      dragging:   false,
      resizeDir:  null,
      dragStart:  null,
      frameStart: null,
      showTip:    true,
      capturing:  false
    }
  },

  computed: {
    frameStyle () {
      return {
        left:   `${this.frame.x}px`,
        top:    `${this.frame.y}px`,
        width:  `${this.frame.w}px`,
        height: `${this.frame.h}px`
      }
    },
    maskTop () {
      return { left: 0, top: 0, right: 0, height: `${this.frame.y}px` }
    },
    maskBottom () {
      return { left: 0, bottom: 0, right: 0, top: `${this.frame.y + this.frame.h}px` }
    },
    maskLeft () {
      return { left: 0, top: `${this.frame.y}px`, width: `${this.frame.x}px`, height: `${this.frame.h}px` }
    },
    maskRight () {
      return { left: `${this.frame.x + this.frame.w}px`, top: `${this.frame.y}px`, right: 0, height: `${this.frame.h}px` }
    }
  },

  watch: {
    visible (val) {
      if (val) {
        this._resetFrame()
        this.showTip   = true
        this.capturing = false
        setTimeout(() => { this.showTip = false }, 4000)
      }
    }
  },

	mounted () {
		document.body.appendChild(this.$el)
	},

	beforeDestroy () {
		if (this.$el && this.$el.parentNode === document.body) {
			document.body.removeChild(this.$el)
		}
	},

  methods: {
    _resetFrame () {
      const pw = window.innerWidth
      const ph = window.innerHeight
      const w  = Math.round(pw * 0.55)
      const h  = Math.round(ph * 0.55)
      this.frame = {
        x: Math.round((pw - w) / 2),
        y: Math.round((ph - h) / 2),
        w,
        h
      }
    },

    _clientPos (e) {
      if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
      if (e.changedTouches && e.changedTouches[0]) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }
      return { x: e.clientX, y: e.clientY }
    },

    // ── Drag frame ────────────────────────────────────────────────────────────
    onFrameMouseDown (e) {
      if (e.button !== 0) return
      this._startDrag(e)
    },
    onFrameTouchStart (e) {
      this._startDrag(e)
    },
    _startDrag (e) {
      this.dragging   = true
      this.resizeDir  = null
      this.showTip    = false
      const pos = this._clientPos(e)
      this.dragStart  = { x: pos.x, y: pos.y }
      this.frameStart = { ...this.frame }
    },

    // ── Resize ────────────────────────────────────────────────────────────────
    startResize (dir, e) {
      this.resizeDir  = dir
      this.dragging   = false
      this.showTip    = false
      const pos = this._clientPos(e)
      this.dragStart  = { x: pos.x, y: pos.y }
      this.frameStart = { ...this.frame }
    },
    startResizeTouch (dir, e) {
      this.startResize(dir, e)
    },

    // ── Mouse/touch move ─────────────────────────────────────────────────────
    onMouseMove (e) { this._handleMove(e) },
    onTouchMove (e) { this._handleMove(e) },

    _handleMove (e) {
      if (!this.dragging && !this.resizeDir) return
      const pos = this._clientPos(e)
      const dx  = pos.x - this.dragStart.x
      const dy  = pos.y - this.dragStart.y
      const vw  = window.innerWidth
      const vh  = window.innerHeight
      const fs  = this.frameStart

      if (this.dragging) {
        this.frame.x = Math.max(0, Math.min(vw - fs.w, fs.x + dx))
        this.frame.y = Math.max(0, Math.min(vh - fs.h, fs.y + dy))
        return
      }

      let { x, y, w, h } = { ...fs }
      const dir = this.resizeDir

      if (dir.includes('e')) { w = Math.max(MIN_SIZE, fs.w + dx) }
      if (dir.includes('s')) { h = Math.max(MIN_SIZE, fs.h + dy) }
      if (dir.includes('w')) {
        const newW = Math.max(MIN_SIZE, fs.w - dx)
        x = fs.x + (fs.w - newW)
        w = newW
      }
      if (dir.includes('n')) {
        const newH = Math.max(MIN_SIZE, fs.h - dy)
        y = fs.y + (fs.h - newH)
        h = newH
      }

      // Clamp
      if (x < 0)       { w += x;      x = 0 }
      if (y < 0)       { h += y;      y = 0 }
      if (x + w > vw)  { w = vw - x }
      if (y + h > vh)  { h = vh - y }
      if (w < MIN_SIZE) w = MIN_SIZE
      if (h < MIN_SIZE) h = MIN_SIZE

      this.frame = { x, y, w, h }
    },

    // ── Mouse/touch up ───────────────────────────────────────────────────────
    onMouseUp ()  { this._endDrag() },
    onTouchEnd () { this._endDrag() },
    _endDrag () {
      this.dragging  = false
      this.resizeDir = null
    },

    // ── Capture ──────────────────────────────────────────────────────────────
    async doCapture () {
      if (this.capturing) return
      this.capturing = true

      try {
        const blob = await this._captureRegion()
        if (!blob) throw new Error('No se pudo capturar la imagen')
        const file = new File([blob], `emplazamiento_${Date.now()}.png`, { type: 'image/png' })
        this.$emit('captured', file)
      } catch (err) {
        console.error('[CaptureFrameOverlay] Error capturando:', err)
        this.$emit('capture-error', err)
      } finally {
        this.capturing = false
      }
    },

    async _captureRegion () {
      const { x, y, w, h } = this.frame

      if (this.viewerType === 'image') {
        return this._captureCanvas(x, y, w, h)
      } else {
        return this._captureForge(x, y, w, h)
      }
    },

    // Captura desde el canvas de ImagePolygonViewer
    _captureCanvas (fx, fy, fw, fh) {
      return new Promise((resolve, reject) => {
        try {
          // El viewerEl puede ser el canvas directamente o el contenedor
          let sourceCanvas = this.viewerEl
          if (sourceCanvas && sourceCanvas.tagName !== 'CANVAS') {
            sourceCanvas = sourceCanvas.querySelector('canvas')
          }
          if (!sourceCanvas) return resolve(null)

          const canvasRect = sourceCanvas.getBoundingClientRect()
          // Coordenadas del frame relativas al canvas
          const relX = fx - canvasRect.left
          const relY = fy - canvasRect.top

          // Ratio entre píxeles lógicos (CSS) y píxeles del canvas
          const scaleX = sourceCanvas.width  / canvasRect.width
          const scaleY = sourceCanvas.height / canvasRect.height

          const sx = Math.max(0, Math.round(relX * scaleX))
          const sy = Math.max(0, Math.round(relY * scaleY))
          const sw = Math.min(Math.round(fw * scaleX), sourceCanvas.width  - sx)
          const sh = Math.min(Math.round(fh * scaleY), sourceCanvas.height - sy)
					
          const out = document.createElement('canvas')
          out.width  = sw
          out.height = sh
          const ctx  = out.getContext('2d')
          ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh)
          out.toBlob(b => resolve(b), 'image/png', 0.92)
        } catch (e) {
          reject(e)
        }
      })
    },

    // Captura desde el div del Forge viewer (html2canvas o getScreenShot nativo)
    async _captureForge (fx, fy, fw, fh) {
      const viewerDiv = this.viewerEl
      if (!viewerDiv) return null

      // Intentar con el método nativo de Autodesk Viewer 7.x
      const viewer = this._getForgeViewerInstance()
      if (viewer && typeof viewer.getScreenShot === 'function') {
        return new Promise((resolve) => {
          // getScreenShot toma toda la vista; recortamos luego
          viewer.getScreenShot(viewer.container.clientWidth, viewer.container.clientHeight, (blobURL) => {
            const img = new Image()
            img.onload = () => {
              const rect    = viewerDiv.getBoundingClientRect()
              const relX    = fx - rect.left
              const relY    = fy - rect.top
              const scaleX  = img.width  / rect.width
              const scaleY  = img.height / rect.height
              const sx = Math.max(0, Math.round(relX * scaleX))
              const sy = Math.max(0, Math.round(relY * scaleY))
              const sw = Math.min(Math.round(fw * scaleX), img.width  - sx)
              const sh = Math.min(Math.round(fh * scaleY), img.height - sy)
              const out = document.createElement('canvas')
              out.width  = sw
              out.height = sh
              out.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)
              out.toBlob(b => resolve(b), 'image/png', 0.92)
            }
            img.onerror = () => resolve(null)
            img.src = blobURL
          })
        })
      }

      // Fallback: html2canvas (si está disponible como global o importado)
      if (typeof html2canvas !== 'undefined') {
        // eslint-disable-next-line no-undef
        const fullCanvas = await html2canvas(viewerDiv, { useCORS: true, allowTaint: true, scale: 1 })
        const rect  = viewerDiv.getBoundingClientRect()
        const relX  = fx - rect.left
        const relY  = fy - rect.top
        const out   = document.createElement('canvas')
        out.width   = fw
        out.height  = fh
        out.getContext('2d').drawImage(fullCanvas, relX, relY, fw, fh, 0, 0, fw, fh)
        return new Promise(res => out.toBlob(b => res(b), 'image/png', 0.92))
      }

      console.warn('[CaptureFrameOverlay] No se encontró método de captura para Forge viewer')
      return null
    },

    // Intenta obtener la instancia del viewer Autodesk desde el viewerEl
    _getForgeViewerInstance () {
			try {
				const el = this.viewerEl
				if (!el) return null
				// Intentar desde la propiedad expuesta por ForgePolygonViewer
				if (el.__forgeViewerInstance) return el.__forgeViewerInstance
				// Buscar en el padre o hijos si el viewerEl es el contenedor raíz del componente
				const forgeDiv = el.querySelector('[id^="forgeViewer-reusable-"]')
				if (forgeDiv && forgeDiv.__forgeViewerInstance) return forgeDiv.__forgeViewerInstance
				return null
			} catch (e) {
				return null
			}
		}
  }
}
</script>

<style scoped>
/* ── Root ── */
.cfo-root {
  position: fixed;
  inset: 0;
  z-index: 9999;
  cursor: default;
  user-select: none;
}

/* ── Fade ── */
.cfo-fade-enter-active,
.cfo-fade-leave-active { transition: opacity 0.2s ease; }
.cfo-fade-enter,
.cfo-fade-leave-to     { opacity: 0; }

/* ── Tip ── */
.cfo-tip {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 20, 35, 0.92);
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  z-index: 10001;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.12);
  pointer-events: none;
}
.cfo-tip-fade-enter-active,
.cfo-tip-fade-leave-active { transition: opacity 0.5s ease; }
.cfo-tip-fade-enter,
.cfo-tip-fade-leave-to     { opacity: 0; }

/* ── Máscaras oscuras ── */
.cfo-mask {
  position: fixed;
  background: rgba(0, 0, 0, 0.58);
  z-index: 10000;
  /* No capturar eventos: permite que elementos sobrepuestos reciban clicks */
  pointer-events: none;
}
.cfo-mask--top    { }
.cfo-mask--bottom { }
.cfo-mask--left   { }
.cfo-mask--right  { }

/* ── Frame ── */
.cfo-frame {
  position: fixed;
  z-index: 10001;
  cursor: move;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.4),
    inset 0 0 0 1px rgba(0,0,0,0.3);
  /* Asegurar que el frame capte eventos incluso si .cfo-mask está presente */
  pointer-events: auto;
}

/* ── Regla de tercios ── */
.cfo-grid { position: absolute; inset: 0; pointer-events: none; }
.cfo-grid__v,
.cfo-grid__h {
  position: absolute;
  background: rgba(255,255,255,0.18);
}
.cfo-grid__v { top: 0; bottom: 0; width: 1px; }
.cfo-grid__h { left: 0; right: 0; height: 1px; }
.cfo-grid__v--1 { left: 33.33%; }
.cfo-grid__v--2 { left: 66.66%; }
.cfo-grid__h--1 { top: 33.33%; }
.cfo-grid__h--2 { top: 66.66%; }

/* ── Badge dimensiones ── */
.cfo-dimensions {
  position: absolute;
  bottom: -26px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20,20,35,0.85);
  color: rgba(255,255,255,0.9);
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  backdrop-filter: blur(2px);
  letter-spacing: 0.04em;
}

/* ── Handles (esquinas) ── */
.cfo-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  border: 2px solid #2D4B95;
  border-radius: 3px;
  z-index: 10002;
  box-shadow: 0 1px 4px rgba(0,0,0,0.35);
}
.cfo-handle--nw { top:  -7px; left:  -7px; cursor: nw-resize; }
.cfo-handle--ne { top:  -7px; right: -7px; cursor: ne-resize; }
.cfo-handle--sw { bottom: -7px; left:  -7px; cursor: sw-resize; }
.cfo-handle--se { bottom: -7px; right: -7px; cursor: se-resize; }

/* ── Edges (lados) ── */
.cfo-edge {
  position: absolute;
  z-index: 10001;
}
.cfo-edge--n, .cfo-edge--s {
  left: 14px; right: 14px; height: 10px;
  cursor: n-resize;
}
.cfo-edge--n { top:   -5px; cursor: n-resize; }
.cfo-edge--s { bottom:-5px; cursor: s-resize; }
.cfo-edge--w, .cfo-edge--e {
  top: 14px; bottom: 14px; width: 10px;
  cursor: w-resize;
}
.cfo-edge--w { left:  -5px; cursor: w-resize; }
.cfo-edge--e { right: -5px; cursor: e-resize; }

/* ── Barra de acciones ── */
.cfo-actions {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10002;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(18, 18, 30, 0.88);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  padding: 8px 14px;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
  /* Asegurar que la barra de acciones reciba eventos */
  pointer-events: auto;
}
.cfo-btn {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  letter-spacing: 0.02em;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}
.cfo-btn { position: relative; z-index: 10003; }
.cfo-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.cfo-btn--cancel {
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.85);
  border: 1px solid rgba(255,255,255,0.2);
}
.cfo-btn--cancel:hover:not(:disabled) { background: rgba(255,255,255,0.18); }
.cfo-btn--capture {
  background: #2D4B95;
  color: #fff;
}
.cfo-btn--capture:hover:not(:disabled) { background: #3a5cb5; }
</style>